import { Box, IconButton, Typography } from "@mui/material"
import { FileCopy, Visibility, VisibilityOff } from "@mui/icons-material"
import type { SxProps, Theme } from "@mui/material"
import CopyableTextField from "components/copyable-text-field"

const CONTAINER_SX = { display: "flex", alignItems: "center", gap: 1 } satisfies SxProps<Theme>
const SECRET_FIELD_SX = { flexGrow: 1 } satisfies SxProps<Theme>
const MASKED_SX = { fontFamily: "monospace" } satisfies SxProps<Theme>

interface PatSecretFieldProps {
  tokenId: string
  prefix: string
  /** Absent on a caller that never displays a token, which leaves the field masked for good. */
  revealedSecret?: string
  disabled: boolean
  // `label` names the token for the reveal/copy button accessible names (e.g. "read-only
  // token"). It is not part of the plan's documented prop list — it is a presentation-only
  // addition needed to keep the profile card's existing aria-labels, and by extension its
  // tests, unchanged after this extraction.
  label: string
  // Optional, not required-but-nullable: a caller that offers only one of the two affordances
  // omits the other rather than spelling out `undefined` for it.
  onCopy?: () => void
  onToggleReveal?: () => void
}

// Pure presentation: which affordances render is entirely up to the caller. The three props
// are independent, so a caller picks any combination:
// - `onToggleReveal` adds the reveal button, for a caller that reveals then copies from the
//   revealed field (the profile card).
// - `onCopy` adds the Copy button, for a caller that copies without displaying anything (the
//   admin grid, whose Copy path writes the secret straight to the clipboard and passes no
//   `revealedSecret` at all).
// - A set `revealedSecret` renders the value in a `CopyableTextField`, whoever put it there.
//   Absent it, the field shows the masked prefix.
export default function PatSecretField({
  tokenId,
  prefix,
  revealedSecret,
  disabled,
  label,
  onCopy,
  onToggleReveal,
}: PatSecretFieldProps) {
  return <Box sx={CONTAINER_SX}>
    <Box sx={SECRET_FIELD_SX}>
      {revealedSecret
        ? <CopyableTextField id={`pat-${tokenId}`} label={null} value={revealedSecret} useValueChangeEffect={false} />
        : <Typography sx={MASKED_SX}>{`${prefix}••••••••`}</Typography>}
    </Box>
    {onCopy && (
      <IconButton aria-label={`Copy ${label}`} onClick={onCopy} size="small" disabled={disabled}>
        <FileCopy />
      </IconButton>
    )}
    {onToggleReveal && (
      <IconButton
        aria-label={`${revealedSecret ? "Hide" : "Reveal"} ${label}`}
        onClick={onToggleReveal}
        size="small"
        disabled={disabled}
      >
        {revealedSecret ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    )}
  </Box>
}
